import Vue from "vue";
import moment from "moment";
import * as types from "./mutation-types";

const MODES = {
  member: "member",
  team: "team",
  packageBuilder: "package-builder"
};

const challengeBuilderParams = state => {
  const formData = new FormData();

  Vue._.each(state.formData, (prop, key) => {
    const propValue = prop.value;
    const propType = prop.typeForApi;

    switch (propType) {
      case "string":
        if (key === "startDate") {
          formData.append(key, moment(propValue).format(prop.dateFormat));
        } else if (key === "endDate") {
          formData.append(key, moment(propValue).format(prop.dateFormat));
        } else {
          formData.append(key, propValue ? propValue : "");
        }

        if (key === "between" && propValue === "groups") {
          Vue._.each(state.stagedTeams, team => {
            formData.append("teamIds[]", team.id);
          });
          Vue._.each(state.stagedOrganizations, org => {
            formData.append("organizationIds[]", org.id);
          });
        } else if (key === "between" && propValue === "individuals") {
          Vue._.each(state.stagedOrganizations, org => {
            formData.append("organizationIds[]", org.id);
          });
          Vue._.each(state.stagedMembers, member => {
            formData.append("memberIds[]", member.id);
          });
        }
        break;
      case "number":
        if (key === "steps") {
          formData.append(
            key,
            parseInt(String(propValue).replace(/\D/, ""), 10)
          );
        } else if (key === "mapId" && propValue === null) {
          // For Chart-based challenges we don't pass mapId
        } else {
          formData.append(key, Number(propValue));
        }
        break;
      case "image":
        if (key === "challengeUploadedImage" && propValue) {
          if (propValue instanceof File) {
            formData.append(key, propValue, propValue.name);
          }
          // If it's a Blob, convert it to a File with proper name
          else if (propValue instanceof Blob) {
            const file = new File([propValue], propValue.name || "image.png", {
              type: propValue.type,
              lastModified: new Date().getTime()
            });
            formData.append(key, file, file.name);
          }
        }
        break;
    }
  });

  return formData;
};
const organizationChallengeBuilderParams = (state, payload) => {
  const formData = {};
  formData.organizationId = payload.organizationId;
  Vue._.each(state.formDataDefaults, (prop, key) => {
    const propValue = prop.value;
    const propType = prop.typeForApi;
    if (key === "mapId") {
      return true;
    }
    switch (propType) {
      case "string":
        formData[key] = propValue ? propValue : "";

        if (key === "startDate") {
          formData[key] = moment(payload.startDate).format(prop.dateFormat);
        }
        if (key === "endDate") {
          formData[key] = moment(payload.endDate).format(prop.dateFormat);
        }

        if (key === "between") {
          formData[key] = "organization";
        }

        if (key === "challengeName") {
          formData[key] = payload.challengeName;
        }
        break;
      case "number":
        formData[key] = Number(propValue);
        if (key === "steps") {
          formData[key] = parseInt(String(payload.steps).replace(/\D/, ""), 10);
        }
        if (key === "duration") {
          formData[key] = parseInt(
            String(payload.duration).replace(/\D/, ""),
            10
          );
        }
        break;
    }
  });
  return formData;
};

const actions = {
  updateFormData({ commit }, data) {
    if (Vue._.isArrayLike(data)) {
      Object.keys(data).forEach(index => {
        commit(types.UPDATE_FORM_DATA, data[index]);
      });
    } else {
      commit(types.UPDATE_FORM_DATA, data);
    }
  },

  updateOption({ commit }, data) {
    commit(types.UPDATE_OPTION, data);
  },

  updateActiveStep({ commit }, step) {
    commit(types.SET_ACTIVE_STEP, step);
  },

  resetForm({ commit, state }) {
    commit(types.SET_FORM_DATA, Vue._.cloneDeep(state.formDataDefaults));
    commit(types.SET_OPTIONS, Vue._.cloneDeep(state.optionsDefaults));
    commit(types.SET_ACTIVE_STEP, 1);
    commit(types.SET_STAGED_TEAMS, []);
    commit(types.SET_MEMBERS, []);
    commit(types.SET_ORGNIZATIONS, []);
    commit(types.SET_SELECTED_PACKAGE, null);
  },

  createChallenge({ commit, state, dispatch }) {
    const formData = challengeBuilderParams(state);

    return Vue.axios
      .post("challenges", formData)
      .then(response => {
        commit("challenges/SET_REFRESH_JOINED_STATUS_NEEDED", true, {
          root: true
        });
        return response;
      })
      .catch(error => {
        dispatch(
          "snackBar",
          {
            message: error.response.data.error.message,
            type: "is-danger",
            queue: false,
            duration: 10000
          },
          { root: true }
        );

        throw error.response;
      });
  },
  createOrganizationChallenge({ state }, payload) {
    const formData = organizationChallengeBuilderParams(state, payload);
    return Vue.axios.post("challenges", formData);
  },

  createPackage({ state, dispatch }) {
    const formData = challengeBuilderParams(state);

    return Vue.axios.post("challenge-packages", formData).catch(error => {
      dispatch(
        "snackBar",
        {
          message: error.response.data.error.message,
          type: "is-danger",
          queue: false,
          duration: 10000
        },
        { root: true }
      );
    });
  },

  setModes({ commit }, mode) {
    commit(types.SET_MEMBER_MODE, MODES.member === mode);
    commit(types.SET_TEAM_MODE, MODES.team === mode);
    commit(types.SET_PACKAGE_MODE, MODES.packageBuilder === mode);
  },

  setSelectedPackage({ commit }, cPackage) {
    commit(types.SET_SELECTED_PACKAGE, cPackage);
    commit(types.UPDATE_FORM_DATA, { key: "packageId", value: cPackage.id });
  },

  stageMember({ commit }, member) {
    commit(types.ADD_MEMBER, member);
  },

  unstageMember({ commit }, memberIndex) {
    commit(types.REMOVE_MEMBER, memberIndex);
  },

  emptyStagedMembers({ commit }) {
    commit(types.EMPTY_STAGED_MEMBERS);
  },

  stageTeam({ commit }, team) {
    commit(types.ADD_TEAM, team);
  },

  unstageTeam({ commit }, teamIndex) {
    commit(types.REMOVE_TEAM, teamIndex);
  },

  emptyStagedTeams({ commit }) {
    commit(types.EMPTY_STAGED_TEAMS);
  },

  stageOrganization({ commit }, orgnization) {
    commit(types.ADD_ORGNIZATION, orgnization);
  },

  unstageOrganization({ commit }, orgnizationIndex) {
    commit(types.REMOVE_ORGNIZATION, orgnizationIndex);
  },

  emptyStagedOrganizations({ commit }) {
    commit(types.EMPTY_STAGED_ORGNIZATIONS);
  }
};

export default actions;
